/**
 * Pool accessor for pre-generated infinite mode levels.
 *
 * At build time, levels are generated and validated into tier JSON files.
 * At runtime, this module picks from the appropriate tier pool based on
 * the current wave, using a seeded random selection that avoids repeats.
 */
import type { Position } from '@/types/game';
import type { WorldEnemyType } from '@/types/enemy';
import type { InfiniteLevelData } from '@/game/infiniteLevelGenerator';

import tier1Data from './tier1.json';
import tier2Data from './tier2.json';
import tier3Data from './tier3.json';
import tier4Data from './tier4.json';
import tier5Data from './tier5.json';
import tier6Data from './tier6.json';

// Compact JSON format matching the generation script output
interface CompactLevel {
  g: string[];
  w: number;
  s: [number, number];
  e: [number, number];
  c: [number, number][];
  n: Array<{ t: WorldEnemyType; p: [number, number] }>;
}

const TIER_POOLS: CompactLevel[][] = [
  tier1Data as CompactLevel[],
  tier2Data as CompactLevel[],
  tier3Data as CompactLevel[],
  tier4Data as CompactLevel[],
  tier5Data as CompactLevel[],
  tier6Data as CompactLevel[],
];

/** Maps a wave number (1-based) to a tier index (0-based). */
export function waveToTier(wave: number): number {
  if (wave <= 3) return 0;
  if (wave <= 6) return 1;
  if (wave <= 10) return 2;
  if (wave <= 15) return 3;
  if (wave <= 20) return 4;
  return 5;
}

/** Converts a compact JSON level back into the full InfiniteLevelData shape. */
function deserialize(compact: CompactLevel): InfiniteLevelData {
  return {
    grid: compact.g,
    gridWidth: compact.w,
    start: compact.s as Position,
    end: compact.e as Position,
    collectibles: compact.c.map(p => p as Position),
    enemies: compact.n.map(e => ({ type: e.t, position: e.p as Position })),
  };
}

/** Simple seedable PRNG (same LCG as the generator). */
function seededRandom(seed: number): number {
  const m = 0x80000000;
  const a = 1103515245;
  const c = 12345;
  const state = (a * seed + c) % m;
  return state / (m - 1);
}

/**
 * Picks a level from the pre-generated pool for the given wave.
 *
 * @param wave - Current wave number (1-based)
 * @param seed - Session seed for deterministic but varied selection
 * @param usedIndices - Set of tier-local indices already played this session
 * @returns The deserialized level data and the tier-local index used
 */
export function pickInfiniteLevel(
  wave: number,
  seed: number,
  usedIndices: Set<number>,
): { level: InfiniteLevelData; levelIndex: number } {
  const tierIdx = waveToTier(wave);
  const pool = TIER_POOLS[tierIdx];
  const poolSize = pool.length;

  // If all levels have been used, wrap around (reset used set for this tier)
  let available: number[];
  if (usedIndices.size >= poolSize) {
    // Pool exhausted - allow reuse
    available = Array.from({ length: poolSize }, (_, i) => i);
  } else {
    available = [];
    for (let i = 0; i < poolSize; i++) {
      if (!usedIndices.has(i)) {
        available.push(i);
      }
    }
  }

  // Seeded selection from available indices
  const hashSeed = seed + wave * 7919 + usedIndices.size * 31;
  const r = seededRandom(hashSeed);
  const chosenIdx = available[Math.floor(r * available.length)];

  return {
    level: deserialize(pool[chosenIdx]),
    levelIndex: chosenIdx,
  };
}

/** Returns the pool size for a given tier (useful for tests). */
export function getTierPoolSize(tier: number): number {
  if (tier < 0 || tier >= TIER_POOLS.length) return 0;
  return TIER_POOLS[tier].length;
}

/** Returns all levels in a tier (for validation tests). */
export function getTierPool(tier: number): InfiniteLevelData[] {
  if (tier < 0 || tier >= TIER_POOLS.length) return [];
  return TIER_POOLS[tier].map(deserialize);
}
